import type { InputWidthType } from '@template-ui/types/ez-form.types';

/** Persisted presentation only; omitted members retain one full-width field per row. */
export interface WorkflowFormLayout {
  width?: InputWidthType | null;
  row?: 'new' | 'previous' | null;
}

export interface WorkflowFormLayoutField {
  control: string;
  layout?: WorkflowFormLayout | null;
}

export interface WorkflowResolvedFormLayout {
  width: InputWidthType;
  row: number;
}

/** Gate each nested form contract by its outer format; capture and provided v3 are distinct shapes. */
export function isWorkflowFormVersionAvailable(
  payloadSchemaVersion: string | null | undefined,
  formOrConfiguration: unknown,
): boolean {
  if (formOrConfiguration == null) return true;
  if (typeof formOrConfiguration !== 'object' || Array.isArray(formOrConfiguration)) return false;
  const value = formOrConfiguration as Record<string, unknown>;
  if (!Array.isArray(value['fields']) || value['fields'].some(field => !field || typeof field !== 'object' || Array.isArray(field))) return false;
  const version = value['version'];
  if (version === 1) return true;
  if (version === 2) return ['1.19', '1.20', '1.21', '1.22'].includes(payloadSchemaVersion ?? '');
  if (version !== 3) return false;
  const fields = value['fields'] as Record<string, unknown>[];
  const capture = typeof value['id'] === 'string' && typeof value['title'] === 'string'
    && fields.every(field => typeof field['id'] === 'string' && !Object.hasOwn(field, 'name'));
  const provided = !Object.hasOwn(value, 'id') && !Object.hasOwn(value, 'title')
    && fields.every(field => typeof field['name'] === 'string' && !Object.hasOwn(field, 'id'));
  return capture && ['1.20', '1.21', '1.22'].includes(payloadSchemaVersion ?? '')
    || provided && ['1.21', '1.22'].includes(payloadSchemaVersion ?? '');
}

/** Recognize only the versioned, declarative layout shape; null retains omission semantics. */
export function isWorkflowFormLayoutSupported(version: number, layout: unknown): boolean {
  if (![1, 2, 3].includes(version)) return false;
  if (layout == null) return true;
  if (version === 1 || typeof layout !== 'object' || Array.isArray(layout)
    || Object.keys(layout).some(key => key !== 'width' && key !== 'row')) return false;
  const value = layout as WorkflowFormLayout;
  return (value.width == null || ['1x', '2x', '3x', '4x', '5x', 'fill'].includes(value.width))
    && (value.row == null || ['new', 'previous'].includes(value.row));
}

/** Resolve positional groups before visibility filtering, without changing the saved fields. */
export function resolveWorkflowFormLayout(
  version: number,
  fields: readonly WorkflowFormLayoutField[],
): WorkflowResolvedFormLayout[] | null {
  if (![1, 2, 3].includes(version)) return null;
  const resolved: WorkflowResolvedFormLayout[] = [];
  let row = 0;
  for (const field of fields) {
    const layout: unknown = field.layout;
    if (!isWorkflowFormLayoutSupported(version, layout)) return null;
    const value = layout as WorkflowFormLayout | null | undefined;
    const width = value?.width ?? 'fill';
    const placement = value?.row ?? 'new';
    if (!['1x', '2x', '3x', '4x', '5x', 'fill'].includes(width) || !['new', 'previous'].includes(placement)) return null;
    if (placement === 'new') row++;
    else if (!row) return null;
    resolved.push({ width, row });
  }
  return resolved;
}
